import { router } from "@inertiajs/react";
import { Autocomplete, Button, Select } from "@mantine/core";
import { useForm } from "@mantine/form";
import { IconPlus } from "@tabler/icons-react";
import type { DataTableSortStatus } from "mantine-datatable";
import { useState } from "react";
import { GlobalDocumentTable } from "../../../components/document/global/GlobalDocumentTable";
import { FilterSection } from "../../../components/filter/FilterSection";
import { openFile } from "../../../helpers/fileHelper";
import { showDeleteModal } from "../../../helpers/modal";
import { useYearContext } from "../../../hooks/useYearContext";
import { ContentShell } from "../../../layout/ContentShell";
import type { Document } from "../../../models/document";
import { getOccupationOptions, type Occupation } from "../../../models/enums/occupation";
import { type Group, getGroupOptions } from "../../../models/group";
import type { Pagination } from "../../../models/pagination";
import { getYearContextOptions, type YearContext } from "../../../models/yearContext";
import { edit_global_document_path, global_document_path, global_documents_path, new_global_document_path } from "../../../routes";

type GlobalDocumentsProps = {
  documents: Document[];
  document_titles: string[];
  groups: Group[];
  year_contexts: YearContext[];
  filter: FilterFormType & {
    sort_key?: "title" | "year_context" | "occupation" | "group.name";
    sort_direction?: "asc" | "desc";
  };
  pagination: Pagination;
};

type FilterFormType = {
  title?: string;
  year_context_id?: string;
  group_id?: string;
  occupation?: Occupation;
};

const resetFilters: FilterFormType = {
  title: undefined,
  year_context_id: undefined,
  group_id: undefined,
  occupation: undefined,
};

export default function GlobalDocuments({ documents, filter, year_contexts, groups, document_titles, pagination }: GlobalDocumentsProps) {
  const { pathWithYearContext } = useYearContext();
  const [isFetching, setIsFetching] = useState(false);

  const { sort_key, sort_direction, ...filterFormData } = filter;

  const sortStatus: DataTableSortStatus<Document> = {
    columnAccessor: sort_key || "",
    direction: sort_direction || "asc",
  };

  const filterForm = useForm<FilterFormType>({
    mode: "uncontrolled",
    initialValues: {
      title: filter?.title,
      year_context_id: filter?.year_context_id,
      group_id: filter?.group_id,
      occupation: filter?.occupation,
    },
  });

  const onNew = () => router.get(pathWithYearContext(new_global_document_path));
  const onEdit = (document: Document) => router.get(pathWithYearContext(edit_global_document_path, document.id));
  const onView = (document: Document) => openFile(pathWithYearContext(global_document_path, document.id));
  const onDelete = (document: Document) =>
    showDeleteModal({
      title: `Globales Dokument ${document.title}`,
      article: "das",
      onConfirm: () => router.delete(pathWithYearContext(global_document_path, document.id)),
    });

  const handlePageUpdate = (filter: FilterFormType, sortStatus: DataTableSortStatus<Document>, page: number) => {
    setIsFetching(true);
    router.visit(pathWithYearContext(global_documents_path), {
      only: ["documents", "filter", "pagination"],
      data: {
        filter: {
          ...filter,
          sort_key: sortStatus.columnAccessor,
          sort_direction: sortStatus.direction,
        },
        page,
      },
      onFinish: () => setIsFetching(true),
    });
  };
  const onSortChange = (sortStatus: DataTableSortStatus<Document>) => handlePageUpdate(filterFormData, sortStatus, pagination.page);
  const onFilterSubmit = () => handlePageUpdate(filterForm.getValues(), sortStatus, pagination.page);
  const onReset = () => handlePageUpdate(resetFilters, sortStatus, pagination.page);
  const onPageChange = (page: number) => handlePageUpdate(filterFormData, sortStatus, page);

  return (
    <ContentShell
      title="Globale Dokumente"
      actions={
        <Button leftSection={<IconPlus />} variant="light" onClick={onNew}>
          Neu
        </Button>
      }
    >
      <FilterSection
        form={filterForm}
        onSubmit={onFilterSubmit}
        onReset={onReset}
        filters={[
          <Autocomplete data={document_titles} label="Titel" key={filterForm.key("title")} {...filterForm.getInputProps("title")} />,
          <Select
            label="Jahrgang"
            clearable
            data={getYearContextOptions(year_contexts)}
            key={filterForm.key("year_context_id")}
            {...filterForm.getInputProps("year_context_id")}
          />,
          <Select
            label="Beruf"
            clearable
            data={getOccupationOptions()}
            key={filterForm.key("occupation")}
            {...filterForm.getInputProps("occupation")}
          />,
          <Select
            label="Gruppe"
            clearable
            data={getGroupOptions(groups)}
            key={filterForm.key("group_id")}
            {...filterForm.getInputProps("group_id")}
          />,
        ]}
      />
      <GlobalDocumentTable
        documents={documents}
        sortStatus={sortStatus}
        onEdit={onEdit}
        onDelete={onDelete}
        onView={onView}
        onSortStatusChange={onSortChange}
        pagination={pagination}
        onPageChange={onPageChange}
        fetching={isFetching}
      />
    </ContentShell>
  );
}
